/**
 * ProductQuickViewModal Component
 * 
 * Displays a modal dialog with product details when a user clicks on a product
 * in the Image Carousel. Uses existing ImageMetadata data - no additional API calls.
 * 
 * Design: PRODUCT_QUICKVIEW_DESIGN.md
 */

import React, { useState, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Button,
  Chip,
  Backdrop,
  Fade,
  Slide,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import CloseIcon from '@mui/icons-material/Close';
import { ShoppingBagOutlined } from '@mui/icons-material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { ImageMetadata } from '../types/conversation';
import { useAppConfig } from '../contexts/ConfigContext';
import { getTextDirectionStyles } from '../utils/textDirection';

interface ProductQuickViewModalProps {
  open: boolean;
  onClose: () => void;
  product: ImageMetadata | null;
  onAddToCart?: (product: ImageMetadata) => void;
  messageId?: string;
}

// Slide transition for mobile
const SlideTransition = React.forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const ProductQuickViewModal: React.FC<ProductQuickViewModalProps> = ({
  open,
  onClose,
  product,
  onAddToCart,
  messageId: _messageId, // Reserved for tracking
}) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm')); // <600px for fullScreen modal
  const { config } = useAppConfig();
  
  // Image gallery state
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  
  // Reset image index when product changes
  useEffect(() => {
    setCurrentImageIndex(0);
  }, [product]);
  
  // Get all images for the product
  const getProductImages = useCallback((): string[] => {
    if (!product) return [];
    
    // Check for product images array (set by ImageCarousel click handler)
    const productImages = product.metadata?.productImages as string[] | undefined;
    if (productImages && Array.isArray(productImages) && productImages.length > 0) {
      return productImages;
    }
    
    // Check for srcset (responsive images array)
    if (product.srcset && Array.isArray(product.srcset) && product.srcset.length > 0) {
      return product.srcset.map(img => img.url);
    }
    
    // Check for multiple images in metadata (legacy)
    const metadataImages = product.metadata?.images as string[] | undefined;
    if (metadataImages && Array.isArray(metadataImages) && metadataImages.length > 0) {
      return metadataImages;
    }
    
    // Fallback to single image
    return [product.url];
  }, [product]);
  
  const images = getProductImages();
  const hasMultipleImages = images.length > 1;
  
  // Image navigation
  const handlePrevImage = useCallback(() => {
    setCurrentImageIndex(prev => (prev === 0 ? images.length - 1 : prev - 1));
  }, [images.length]);
  
  const handleNextImage = useCallback(() => {
    setCurrentImageIndex(prev => (prev === images.length - 1 ? 0 : prev + 1));
  }, [images.length]);
  
  // Handle add to cart
  const handleAddToCart = useCallback(() => {
    if (!product) return;
    
    if (onAddToCart) {
      onAddToCart(product);
    }
    
    // Open product URL in new tab
    if (product.product_url) {
      window.open(product.product_url, '_blank', 'noopener,noreferrer');
    }
  }, [product, onAddToCart]);
  
  // Keyboard navigation
  useEffect(() => {
    if (!open) return;
    
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      } else if (event.key === 'ArrowLeft' && hasMultipleImages) {
        handlePrevImage();
      } else if (event.key === 'ArrowRight' && hasMultipleImages) {
        handleNextImage();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose, hasMultipleImages, handlePrevImage, handleNextImage]);
  
  // Config-based visibility
  const quickViewConfig = config?.product_quickview;
  const showDescription = quickViewConfig?.show_description !== false;
  const showSizeChips = quickViewConfig?.show_size_chips !== false;
  const showColors = quickViewConfig?.show_colors !== false;
  const showColorPalette = quickViewConfig?.show_color_palette !== false;
  const showCategory = quickViewConfig?.show_category !== false;
  const showStyle = quickViewConfig?.show_style !== false;
  const showSilhouette = quickViewConfig?.show_silhouette !== false;
  const showSeason = quickViewConfig?.show_season !== false;
  const addToCartText = quickViewConfig?.add_to_cart_text || 'Add to Cart';
  const addToCartBgColor = quickViewConfig?.add_to_cart_bg_color || 'primary.main';
  const addToCartTextColor = quickViewConfig?.add_to_cart_text_color || 'white';
  
  // Get text direction styles for value fields based on config and content
  const getValueDirectionStyles = useCallback((value: string | undefined) => {
    if (!value) return {};
    const configDirection = quickViewConfig?.text_direction || 'auto';
    
    if (configDirection === 'auto') {
      return getTextDirectionStyles(value);
    }
    
    // Force direction from config (ltr or rtl)
    return {
      direction: configDirection as 'ltr' | 'rtl',
      textAlign: configDirection === 'rtl' ? 'right' as const : 'left' as const,
      unicodeBidi: 'embed' as const,
    };
  }, [quickViewConfig?.text_direction]);
  
  if (!product) return null;
  
  // Extract product data with fallbacks
  const productTitle = product.product_title || product.title || product.alt_text || 'Product';
  const price = product.price;
  const description = product.product_desc;
  const color = product.color;
  const colorPalette = product.color_palette || [];
  const sizes = product.sizes || [];
  const brand = product.metadata?.brand as string | undefined;
  const itemType = product.item_type;
  const styles = product.style || [];
  const silhouette = product.silhouette;
  const gender = product.gender;
  const season = product.season;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isSmallScreen}
      TransitionComponent={isSmallScreen ? SlideTransition : Fade}
      aria-labelledby="product-quickview-title"
      aria-modal="true"
      BackdropComponent={Backdrop}
      BackdropProps={{
        sx: {
          backgroundColor: 'rgba(0, 0, 0, 0.85)',
        },
      }}
      PaperProps={{
        sx: {
          borderRadius: isSmallScreen ? 0 : 2,
          minHeight: isSmallScreen ? '100%' : '80vh',
          maxHeight: isSmallScreen ? '100%' : '90vh',
          overflow: 'hidden',
        },
      }}
    >
      {/* Close Button */}
      <IconButton
        onClick={onClose}
        aria-label="Close product view"
        sx={{
          position: 'absolute',
          top: { xs: 8, sm: 12 },
          right: { xs: 8, sm: 12 },
          zIndex: 10,
          bgcolor: 'rgba(0, 0, 0, 0.5)',
          color: 'white',
          width: { xs: 36, sm: 40 },
          height: { xs: 36, sm: 40 },
          '&:hover': {
            bgcolor: 'rgba(0, 0, 0, 0.7)',
          },
        }}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent
        sx={{
          p: 0,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          height: isSmallScreen ? 'calc(100% - 70px)' : '100%',
          minHeight: isSmallScreen ? 'auto' : 'calc(80vh - 70px)',
          overflow: 'auto',
        }}
      >
        {/* Image Gallery Section */}
        <Box
          sx={{
            flex: { xs: 'none', md: '0 0 55%' },
            position: 'relative',
            bgcolor: 'grey.100',
            minHeight: { xs: 300, sm: 400, md: '100%' },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {/* Main Image */}
          <Box
            component="img"
            src={images[currentImageIndex]}
            alt={productTitle}
            sx={{
              maxWidth: '100%',
              maxHeight: { xs: 300, sm: 400, md: 'calc(80vh - 100px)' },
              objectFit: 'contain',
            }}
          />
          
          {/* Navigation Arrows */}
          {hasMultipleImages && (
            <>
              <IconButton
                onClick={handlePrevImage}
                sx={{
                  position: 'absolute',
                  left: 8,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  bgcolor: 'rgba(255, 255, 255, 0.9)',
                  '&:hover': { bgcolor: 'rgba(255, 255, 255, 1)' },
                }}
                aria-label="Previous image"
              >
                <ChevronLeftIcon />
              </IconButton>
              <IconButton
                onClick={handleNextImage}
                sx={{
                  position: 'absolute',
                  right: 8,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  bgcolor: 'rgba(255, 255, 255, 0.9)',
                  '&:hover': { bgcolor: 'rgba(255, 255, 255, 1)' },
                }}
                aria-label="Next image"
              >
                <ChevronRightIcon />
              </IconButton>
            </>
          )}
          
          {/* Thumbnail Dots */}
          {hasMultipleImages && (
            <Box
              sx={{
                position: 'absolute',
                bottom: 16,
                left: '50%',
                transform: 'translateX(-50%)',
                display: 'flex',
                gap: 1,
              }}
            >
              {images.map((_, index) => (
                <Box
                  key={index}
                  onClick={() => setCurrentImageIndex(index)}
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: index === currentImageIndex ? 'primary.main' : 'rgba(0, 0, 0, 0.3)',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s',
                    '&:hover': {
                      bgcolor: index === currentImageIndex ? 'primary.main' : 'rgba(0, 0, 0, 0.5)',
                    },
                  }}
                />
              ))}
            </Box>
          )}
        </Box>

        {/* Product Details Section */}
        <Box
          sx={{
            flex: { xs: 1, md: '0 0 45%' },
            p: { xs: 2, sm: 2.5, md: 3 },
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: { xs: 1, sm: 1.5, md: 2 },
          }}
        >
          {/* Brand */}
          {brand && (
            <Typography
              variant="overline"
              sx={{
                color: 'text.secondary',
                letterSpacing: 1.5,
                fontSize: '0.7rem',
                ...getValueDirectionStyles(brand),
              }}
            >
              {brand}
            </Typography>
          )}
          
          {/* Product Title */}
          <Typography
            id="product-quickview-title"
            variant="h6"
            component="h2"
            sx={{
              fontWeight: 600,
              fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.5rem' },
              lineHeight: 1.3,
              ...getValueDirectionStyles(productTitle),
            }}
          >
            {productTitle}
          </Typography>
          
          {/* Price */}
          {price && (
            <Typography
              variant="h6"
              sx={{
                color: 'primary.main',
                fontWeight: 700,
                fontSize: { xs: '1.1rem', sm: '1.25rem' },
              }}
            >
              {price}
            </Typography>
          )}
          
          {/* Description */}
          {showDescription && description && (
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                fontSize: { xs: '0.85rem', sm: '0.9rem' },
                lineHeight: 1.6,
                ...getValueDirectionStyles(description),
              }}
            >
              {description}
            </Typography>
          )}
          
          {/* Colors */}
          {showColors && color && (
            <Box>
              <Typography
                variant="caption"
                sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}
              >
                Color
              </Typography>
              <Chip
                label={color}
                size="small"
                variant="outlined"
                sx={{ fontSize: '0.75rem', ...getValueDirectionStyles(color) }}
              />
            </Box>
          )}
          
          {/* Color Palette */}
          {showColorPalette && colorPalette.length > 0 && (
            <Box>
              <Typography
                variant="caption"
                sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}
              >
                Color Palette
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {colorPalette.map((colorName, index) => (
                  <Chip
                    key={index}
                    label={colorName}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: '0.75rem', ...getValueDirectionStyles(colorName) }}
                  />
                ))}
              </Box>
            </Box>
          )}
          
          {/* Sizes */}
          {showSizeChips && sizes.length > 0 && (
            <Box>
              <Typography
                variant="caption"
                sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}
              >
                Sizes
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {sizes.map((size, index) => (
                  <Chip
                    key={index}
                    label={size}
                    size="small"
                    variant="outlined"
                    sx={{
                      minWidth: 36,
                      fontSize: '0.75rem',
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}
          
          {/* Item Type & Gender */}
          {showCategory && (itemType || gender) && (
            <Box>
              <Typography
                variant="caption"
                sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}
              >
                Category
              </Typography>
              <Typography variant="body2" sx={getValueDirectionStyles(gender && itemType ? `${gender}'s ${itemType}` : (itemType || gender))}>
                {gender && itemType ? `${gender}'s ${itemType}` : (itemType || gender)}
              </Typography>
            </Box>
          )}
          
          {/* Style */}
          {showStyle && styles.length > 0 && (
            <Box>
              <Typography
                variant="caption"
                sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}
              >
                Style
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {styles.map((styleName, index) => (
                  <Chip
                    key={index}
                    label={styleName}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: '0.75rem', ...getValueDirectionStyles(styleName) }}
                  />
                ))}
              </Box>
            </Box>
          )}
          
          {/* Silhouette & Season */}
          {(showSilhouette || showSeason) && (silhouette || season) && (
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              {showSilhouette && silhouette && (
                <Box>
                  <Typography
                    variant="caption"
                    sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}
                  >
                    Silhouette
                  </Typography>
                  <Typography variant="body2" sx={getValueDirectionStyles(silhouette)}>{silhouette}</Typography>
                </Box>
              )}
              {showSeason && season && (
                <Box>
                  <Typography
                    variant="caption"
                    sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}
                  >
                    Season
                  </Typography>
                  <Typography variant="body2" sx={getValueDirectionStyles(season)}>{season}</Typography>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </DialogContent>

      {/* Action Footer */}
      <Box
        sx={{
          p: { xs: 1.5, sm: 2 },
          borderTop: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          position: isSmallScreen ? 'fixed' : 'relative',
          bottom: isSmallScreen ? 0 : 'auto',
          left: 0,
          right: 0,
        }}
      >
        <Button
          variant="contained"
          fullWidth
          size="large"
          startIcon={<ShoppingBagOutlined />}
          onClick={handleAddToCart}
          sx={{
            bgcolor: addToCartBgColor,
            color: addToCartTextColor,
            py: { xs: 0.75, sm: 1 },
            px: 2,
            fontSize: { xs: '0.75rem', sm: '0.875rem' },
            fontWeight: 600,
            textTransform: 'none',
            borderRadius: '20px',
            boxShadow: 'none',
            '& .MuiButton-startIcon': {
              marginRight: '6px',
              marginLeft: 0,
            },
            '&:hover': {
              bgcolor: addToCartBgColor,
              boxShadow: 1,
              filter: 'brightness(0.9)',
            },
          }}
        >
          {addToCartText}
        </Button>
      </Box>
    </Dialog>
  );
};

export default ProductQuickViewModal;
